import { useCallback, useMemo } from 'react';

import { Task } from '@/services/types/client-intake-types';
import { CanonicalTaskStatus } from '@/services/types/task-types';
import {
  getTaskTimeBasedPriority,
  getTimeBasedGroupName,
  getTimeBasedGroupOrder as getTimeBasedOrder,
  isTaskImmediate,
} from '@/utils/taskDateUtils';

import { sortTasksWithinGroup } from './TaskPriorityUtils';

/**
 * Check if a task has a non-active status (Completed, Not Applicable, Closed)
 */
const isTaskNonActive = (task: Task): boolean => {
  return (
    task.status.canonical_status === CanonicalTaskStatus.COMPLETED ||
    task.status.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE ||
    task.status.canonical_status === CanonicalTaskStatus.CLOSED
  );
};

interface UseTaskGroupingOptions {
  /** When true, completed/N/A/closed tasks stay in their time-based group instead of being routed to a "Completed" group */
  keepResolvedInPlace?: boolean;
}

export const useTaskGrouping = (
  tasks: Task[] | undefined,
  options?: UseTaskGroupingOptions,
) => {
  const keepResolvedInPlace = options?.keepResolvedInPlace ?? false;
  const allTasks = useMemo(() => tasks || [], [tasks]);

  const timeBasedGroupedTasks = useMemo(() => {
    return allTasks.reduce(
      (acc, task) => {
        // Non-active tasks (Completed, N/A, Closed) go to Completed group
        // unless keepResolvedInPlace is true
        if (!keepResolvedInPlace && isTaskNonActive(task)) {
          const groupName = 'Completed';
          if (!acc[groupName]) {
            acc[groupName] = [];
          }
          acc[groupName].push(task);
          return acc;
        }

        // Check for immediate tasks
        if (isTaskImmediate(task)) {
          if (!acc.Immediate) {
            acc.Immediate = [];
          }
          acc.Immediate.push(task);
          return acc;
        }

        // Otherwise use time-based grouping
        const priority = getTaskTimeBasedPriority(
          task,
          keepResolvedInPlace ? { ignoreStatus: true } : undefined,
        );
        const groupName = getTimeBasedGroupName(priority);

        if (!acc[groupName]) {
          acc[groupName] = [];
        }
        acc[groupName].push(task);

        return acc;
      },
      {} as Record<string, Task[]>,
    );
  }, [allTasks, keepResolvedInPlace]);

  // Sort tasks within each group
  const sortedTimeBasedGroups = useMemo(() => {
    const result: Record<string, Task[]> = {};
    Object.entries(timeBasedGroupedTasks).forEach(([groupName, groupTasks]) => {
      result[groupName] = sortTasksWithinGroup(groupTasks, groupName);
    });
    return result;
  }, [timeBasedGroupedTasks]);

  const getFilteredAndSortedGroups = useCallback(
    (showCompletedGroups: boolean): [string, Task[]][] => {
      return Object.entries(sortedTimeBasedGroups)
        .filter(([groupName]) => {
          if (groupName === 'Completed') {
            return showCompletedGroups;
          }
          return true;
        })
        .sort(([groupNameA], [groupNameB]) => {
          // Immediate tasks always come first
          if (groupNameA === 'Immediate') return -1;
          if (groupNameB === 'Immediate') return 1;

          // Get time-based priority levels for proper sorting
          const levelA =
            groupNameA === 'Overdue'
              ? 'overdue'
              : groupNameA === 'Next Action Within 24 Hours'
                ? 'due_24hrs'
                : groupNameA === 'Next Action Within 5 Days'
                  ? 'due_5days'
                  : groupNameA === 'Next Action Within 2 Weeks'
                    ? 'due_2weeks'
                    : groupNameA === 'Next Action Beyond 2 Weeks'
                      ? 'beyond'
                      : 'completed';

          const levelB =
            groupNameB === 'Overdue'
              ? 'overdue'
              : groupNameB === 'Next Action Within 24 Hours'
                ? 'due_24hrs'
                : groupNameB === 'Next Action Within 5 Days'
                  ? 'due_5days'
                  : groupNameB === 'Next Action Within 2 Weeks'
                    ? 'due_2weeks'
                    : groupNameB === 'Next Action Beyond 2 Weeks'
                      ? 'beyond'
                      : 'completed';

          return getTimeBasedOrder(levelA) - getTimeBasedOrder(levelB);
        });
    },
    [sortedTimeBasedGroups],
  );

  const getInitialOpenState = useCallback((): Record<string, boolean> => {
    if (!tasks) return {};

    const timeBasedGroups = tasks.reduce(
      (acc, task) => {
        // Non-active tasks go to Completed group (unless keepResolvedInPlace)
        if (!keepResolvedInPlace && isTaskNonActive(task)) {
          acc.Completed = true;
          return acc;
        }

        // Immediate tasks group should be open by default
        if (isTaskImmediate(task)) {
          acc.Immediate = true;
          return acc;
        }

        const priority = getTaskTimeBasedPriority(
          task,
          keepResolvedInPlace ? { ignoreStatus: true } : undefined,
        );
        const groupName = getTimeBasedGroupName(priority);

        acc[groupName] = true;

        return acc;
      },
      {} as Record<string, boolean>,
    );

    return timeBasedGroups;
  }, [tasks, keepResolvedInPlace]);

  return {
    allTasks,
    timeBasedGroupedTasks: sortedTimeBasedGroups,
    getFilteredAndSortedGroups,
    getInitialOpenState,
  };
};
